import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Button,
    Typography,
    Alert,
    useTheme,
    Skeleton,
} from '@mui/material';
import {
    CheckCircle as CompleteIcon,
    Extension as IntegrationIcon,
    Receipt as TransactionIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import Breadcrumb from '../common/Breadcrumb';
import PageLayout from '../common/PageLayout';
import { useAIService } from '../../services/ai.service';
import { TaxableTransaction } from '../tax-reports/types';

interface IssueTransaction extends TaxableTransaction {
    issueType?: 'missingAssetValue' | 'costBasis';
    taxYear?: number;
}

interface ParsedAnalysis {
    analysisSummary: {
        transactionCount: number;
        timeframe: string;
        focusTransaction: string;
    };
    theIssue: {
        title: string;
        explanation: string;
        whatHappened: string[];
        beginnerNote?: string;
    };
    whatToLookFor: {
        items: Array<{
            title: string;
            description: string;
            checked?: boolean;
        }>;
    };
    nextSteps: {
        steps: Array<{
            title: string;
            description: string;
        }>;
        primaryAction?: string;
    };
    technicalDetails?: string;
}

interface AICheckPageProps {
    allIssuesData: IssueTransaction[];
    userCurrency: string;
}

/**
 * Extracts the real transaction ID from the issue data
 * For missing price issues, the issue ID is the transaction ID
 * For cost basis issues, the real transaction ID is found in pool.trades[0].sale_id
 */
const getRealTransactionId = (issueTransaction: IssueTransaction): number => {
    // For missing price issues, the issue ID is the transaction ID
    if (issueTransaction.issueType === 'missingAssetValue') {
        console.log(`[AICheckPage] Using issue ID ${issueTransaction.id} as transaction ID for missing price issue`);
        return Number(issueTransaction.id);
    }

    // For cost basis issues, try to get the real transaction ID from pool.trades[0].sale_id
    const pool = (issueTransaction as any).pool;
    const trades = pool?.trades;

    if (trades && Array.isArray(trades) && trades.length > 0) {
        const saleId = trades[0].sale_id;
        if (saleId && saleId > 0) {
            console.log(`[AICheckPage] Found real transaction ID ${saleId} for cost basis issue ${issueTransaction.id}`);
            return saleId;
        }
    }

    // Fallback for cost basis issues where we can't find the sale_id
    console.warn(`[AICheckPage] Could not find real transaction ID for cost basis issue ${issueTransaction.id}, using issue ID as fallback`);
    return Number(issueTransaction.id);
};

/**
 * Gets transaction analysis data including real transaction ID and context
 */
const getTransactionAnalysisData = (issueTransaction: IssueTransaction) => {
    const realTransactionId = getRealTransactionId(issueTransaction);
    const pool = (issueTransaction as any).pool;
    const transactionType = pool?.transaction_type;
    const missingAmount = issueTransaction.amount || 0;
    const asset = issueTransaction.currency;

    // Determine if this is a fee-related cost basis issue
    const isFeeCostBasisIssue = transactionType === 1000 || // OutVirtualWithdrawalFee
                               transactionType === 1004 || // OutVirtualTradeFee
                               transactionType === 12;     // OutMarginFee

    console.log(`[AICheckPage] Transaction analysis data for issue ${issueTransaction.id}:`, {
        realTransactionId,
        transactionType,
        missingAmount,
        asset,
        isFeeCostBasisIssue
    });

    return {
        realTransactionId,
        transactionType,
        missingAmount,
        asset,
        isFeeCostBasisIssue
    };
};

/**
 * Determines the correct asset symbol to analyze based on transaction type
 * For cost basis issues, we always use the currency field from the issue data
 */
const getAssetSymbolForAnalysis = (transaction: IssueTransaction): string => {
    // For cost basis issues, always use the currency from the issue data
    // This represents the asset that has the missing cost basis problem
    const assetSymbol = (transaction as any).currencySymbol || transaction.currency;

    const analysisData = getTransactionAnalysisData(transaction);
    console.log(`[AICheckPage] Using asset symbol '${assetSymbol}' for analysis of ${analysisData.isFeeCostBasisIssue ? 'fee' : 'sale'} transaction`);

    return assetSymbol;
};

const AICheckPage: React.FC<AICheckPageProps> = ({
    allIssuesData,
    userCurrency,
}) => {
    const { issueId } = useParams<{ issueId: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const aiService = useAIService();

    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [analysis, setAnalysis] = useState<ParsedAnalysis | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [analysisMetadata, setAnalysisMetadata] = useState<{
        transactionCount: number | null;
        dateRange: string | null;
    }>({ transactionCount: null, dateRange: null });
    const analysisStartedRef = useRef(false);

    // Get filter from URL
    const urlFilter = searchParams.get('filter') || 'all';

    // Find the issue from allIssuesData
    const issue = allIssuesData.find(i => i.id?.toString() === issueId);

    // Simplified parsing - just use the AI response directly
    const parseAnalysis = (aiResponse: string, responseMeta?: any): ParsedAnalysis => {
        const analysisSummary = responseMeta?.analysisSummary || {
            transactionCount: 0,
            timeframe: 'Unknown',
            focusTransaction: `#${issueId}`
        };

        const hasContent = aiResponse && aiResponse.trim().length > 10;

        return {
            analysisSummary,
            theIssue: {
                title: 'AI Analysis',
                explanation: hasContent ? aiResponse : 'Analysis not available.',
                whatHappened: [],
                beginnerNote: undefined
            },
            whatToLookFor: {
                items: []
            },
            nextSteps: {
                steps: [],
                primaryAction: undefined
            },
            technicalDetails: aiResponse
        };
    };

    // Load cached analysis or start new analysis on mount
    useEffect(() => {
        if (!issue) return;

        // Try to load from cache first
        const cacheKey = `ai-analysis-${issueId}`;
        const cachedData = sessionStorage.getItem(cacheKey);

        if (cachedData && !analysisStartedRef.current) {
            try {
                const parsed = JSON.parse(cachedData);
                setAnalysis(parsed.analysis);
                setAnalysisMetadata(parsed.metadata);
                console.log('[AICheckPage] Loaded analysis from cache');
                return;
            } catch (error) {
                console.error('[AICheckPage] Error loading cached analysis:', error);
                sessionStorage.removeItem(cacheKey);
            }
        }

        // If no cache or cache failed, run analysis
        if (!analysisStartedRef.current && !isAnalyzing) {
            analysisStartedRef.current = true;
            handleStartAnalysis();
        }
    }, [issue, issueId]);

    const handleStartAnalysis = async () => {
        if (!issue || isAnalyzing) return;

        setIsAnalyzing(true);
        setError(null);
        setAnalysis(null);
        setAnalysisMetadata({ transactionCount: null, dateRange: null });

        try {
            const currentLanguage = i18n.language || 'de';
            const assetSymbol = getAssetSymbolForAnalysis(issue);
            const issueType = issue.issueType || 'costBasis';

            console.log('[AICheckPage] Starting analysis with:', {
                issueId,
                assetSymbol,
                issueType
            });

            // Backend will extract the real transaction ID from issue data
            const result = await aiService.analyzeTransactionIssue(
                issue.id, // Send issue ID - backend will map to real transaction ID
                assetSymbol,
                issueType,
                issue,
                currentLanguage,
                (metadata: { transactionCount?: number; dateRange?: string }) => {
                    setAnalysisMetadata({
                        transactionCount: metadata.transactionCount || null,
                        dateRange: metadata.dateRange || null,
                    });
                }
            );

            if (result.success) {
                const parsed = parseAnalysis(result.analysis, result);
                setAnalysis(parsed);

                const metadata = {
                    transactionCount: result.analysisSummary?.transactionCount || null,
                    dateRange: result.analysisSummary?.timeframe || null,
                };

                if (result.analysisSummary) {
                    setAnalysisMetadata(metadata);
                }

                // Cache the analysis result
                const cacheKey = `ai-analysis-${issueId}`;
                sessionStorage.setItem(cacheKey, JSON.stringify({
                    analysis: parsed,
                    metadata: metadata,
                    timestamp: Date.now()
                }));
                console.log('[AICheckPage] Cached analysis result');
            } else {
                setError(result.error || 'Analysis failed');
            }
        } catch (err: any) {
            console.error('AI Analysis Error:', err);
            setError(err.message || 'Failed to analyze transaction');
        } finally {
            setIsAnalyzing(false);
        }
    };

    const handleViewTransactions = () => {
        if (!issue) return;

        const assetSymbol = getAssetSymbolForAnalysis(issue);

        let endDate = '';
        let startDate = '';

        if (issue.sold_at) {
            const issueDate = new Date(issue.sold_at);
            const endDateTime = new Date(issueDate);
            endDateTime.setHours(endDateTime.getHours() + 24);
            endDate = endDateTime.toISOString();

            const startDateTime = new Date(issueDate);
            startDateTime.setDate(startDateTime.getDate() - 30);
            startDate = startDateTime.toISOString();
        }

        const params = new URLSearchParams();
        params.set('smartCheckContext', 'true');
        params.set('issueType', 'costBasis');
        params.set('issueId', issueId || '');
        params.set('filter', urlFilter);
        if (assetSymbol) params.set('assets', assetSymbol);
        if (startDate) params.set('startDate', startDate);
        if (endDate) params.set('endDate', endDate);

        // No need to save aiAnalysisResults - TransactionPage will load from ai-analysis-${issueId} cache

        navigate(`/transactions?${params.toString()}`);
    };

    // Build breadcrumb
    const getFilterLabel = () => {
        switch (urlFilter) {
            case 'high':
                return t('smartCheck.filters.high');
            case 'low':
                return t('smartCheck.filters.low');
            case 'pricing':
                return t('smartCheck.filters.pricing');
            case 'ignored':
                return t('smartCheck.filters.ignored');
            default:
                return t('smartCheck.filters.all');
        }
    };

    const breadcrumbItems = [
        { label: t('smartCheck.overview.title'), path: '/smart-check' },
        { label: getFilterLabel(), path: `/smart-check/issues?filter=${urlFilter}` },
        { label: 'AI-Check' }
    ];

    if (!issue) {
        return (
            <PageLayout>
                <Breadcrumb items={breadcrumbItems} />
                <Alert severity="error">
                    {t('smartCheck.errors.issueNotFound')}
                </Alert>
            </PageLayout>
        );
    }

    const assetSymbol = getAssetSymbolForAnalysis(issue);

    return (
        <PageLayout>
            <Breadcrumb items={breadcrumbItems} />

            {/* Header */}
            <Box
                sx={{
                    mb: 4,
                    p: 3,
                    borderRadius: 2,
                    background: isAnalyzing
                        ? 'linear-gradient(135deg, rgba(33, 150, 243, 0.05) 0%, rgba(33, 150, 243, 0.02) 100%)'
                        : 'linear-gradient(135deg, rgba(76, 175, 80, 0.05) 0%, rgba(76, 175, 80, 0.02) 100%)',
                    border: '1px solid',
                    borderColor: isAnalyzing ? 'primary.light' : 'success.light',
                    transition: 'all 0.3s ease-in-out'
                }}
            >
                {isAnalyzing ? (
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                            <Box
                                sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: 'primary.main',
                                    animation: 'pulse 2s ease-in-out infinite',
                                    '@keyframes pulse': {
                                        '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                                        '50%': { opacity: 0.5, transform: 'scale(1.2)' }
                                    }
                                }}
                            />
                            <Typography variant="overline" sx={{ fontWeight: 600, color: 'primary.main', letterSpacing: 1 }}>
                                {i18n.language === 'de' ? 'ANALYSE LÄUFT' : 'ANALYZING'}
                            </Typography>
                        </Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}>
                            {assetSymbol}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                {analysisMetadata.transactionCount || '...'} {i18n.language === 'de' ? 'Transaktionen' : 'transactions'}
                            </Typography>
                            {analysisMetadata.dateRange && (
                                <>
                                    <Box component="span" sx={{ color: 'text.disabled' }}>•</Box>
                                    <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                        {analysisMetadata.dateRange}
                                    </Typography>
                                </>
                            )}
                        </Box>
                    </Box>
                ) : analysis ? (
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                            <CompleteIcon sx={{ color: 'success.main', fontSize: 20 }} />
                            <Typography variant="overline" sx={{ fontWeight: 600, color: 'success.main', letterSpacing: 1 }}>
                                {i18n.language === 'de' ? 'ANALYSE ABGESCHLOSSEN' : 'ANALYSIS COMPLETE'}
                            </Typography>
                        </Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}>
                            {assetSymbol}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                {analysis.analysisSummary.transactionCount} {t('smartCheck.aiAnalysis.transactionsAnalyzed')}
                            </Typography>
                            <Box component="span" sx={{ color: 'text.disabled' }}>•</Box>
                            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                {analysis.analysisSummary.timeframe}
                            </Typography>
                        </Box>
                    </Box>
                ) : null}
            </Box>

                {/* Content */}
                <Box sx={{ mb: 4 }}>
                    {isAnalyzing && (
                        <Box>
                            <Box sx={{ mb: 3 }}>
                                <Skeleton variant="text" width="40%" height={28} sx={{ mb: 1 }} />
                                <Skeleton variant="text" width="95%" height={24} sx={{ mb: 0.5 }} />
                                <Skeleton variant="text" width="88%" height={24} sx={{ mb: 0.5 }} />
                                <Skeleton variant="text" width="75%" height={24} sx={{ mb: 2 }} />
                            </Box>
                            <Box sx={{ mb: 3 }}>
                                <Skeleton variant="text" width="35%" height={28} sx={{ mb: 1 }} />
                                <Skeleton variant="text" width="92%" height={24} sx={{ mb: 0.5 }} />
                                <Skeleton variant="text" width="96%" height={24} sx={{ mb: 0.5 }} />
                                <Skeleton variant="text" width="85%" height={24} sx={{ mb: 0.5 }} />
                                <Skeleton variant="text" width="70%" height={24} />
                            </Box>
                        </Box>
                    )}

                    {error && !isAnalyzing && (
                        <Alert
                            severity="error"
                            action={
                                <Button
                                    color="inherit"
                                    size="small"
                                    onClick={handleStartAnalysis}
                                >
                                    {t('common.retry')}
                                </Button>
                            }
                        >
                            {error}
                        </Alert>
                    )}

                    {analysis && !isAnalyzing && (
                        <Typography
                            variant="body1"
                            sx={{
                                whiteSpace: 'pre-wrap',
                                lineHeight: 1.6,
                                '& h3': {
                                    fontSize: '1.1rem',
                                    fontWeight: 'bold',
                                    marginTop: 2,
                                    marginBottom: 1
                                }
                            }}
                        >
                            {analysis.theIssue.explanation}
                        </Typography>
                    )}
                </Box>

                {/* Action Buttons */}
                {analysis && !isAnalyzing && (
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 2,
                            justifyContent: 'center',
                            pt: 3,
                            borderTop: '1px solid',
                            borderTopColor: 'divider',
                        }}
                    >
                        <Button
                            variant="outlined"
                            startIcon={<IntegrationIcon />}
                            onClick={() => navigate('/integrations')}
                            sx={{
                                minWidth: 200,
                                height: 48,
                                fontSize: '0.95rem',
                                fontWeight: 600,
                            }}
                        >
                            {t('smartCheck.aiAnalysis.cta.importMoreData')}
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<TransactionIcon />}
                            onClick={handleViewTransactions}
                            sx={{
                                minWidth: 200,
                                height: 48,
                                fontSize: '0.95rem',
                                fontWeight: 600,
                            }}
                        >
                            {t('smartCheck.aiAnalysis.cta.reviewTransactions')}
                        </Button>
                    </Box>
                )}
        </PageLayout>
    );
};

export default AICheckPage;
